/**
 * Document block map: parse markdown into sections with stable IDs for merge-by-ID.
 * Used by Creator mode vibe edits so the AI can target "section_2" or "section_definitions"
 * even when the doc has no markdown headings (e.g. "Title:", "Definitions" as plain text).
 */

const PREVIEW_MAX_LEN = 80;

/**
 * Detect if a line is a section start:
 * - Markdown heading: # ... ######
 * - Title-like: short line (≤80 chars) ending with colon, or standalone short line before content
 */
function isSectionStart(line, lineIndex, lines) {
  const t = (line || '').trim();
  if (!t) return false;
  if (/^(#{1,6})\s+.+$/.test(t)) return true;
  if (t.length <= 80 && t.endsWith(':')) return true;
  const nextLine = (lines[lineIndex + 1] || '').trim();
  if (t.length <= 80 && !nextLine && lineIndex + 2 < lines.length && (lines[lineIndex + 2] || '').trim()) return true;
  return false;
}

/**
 * Build a block map from document markdown.
 * Returns { mapText, blocks } where blocks have { id, startIndex, endIndex, preview }.
 * Character indices are for the original doc string so we can replace by span.
 */
export function buildDocumentBlockMap(docMarkdown) {
  const doc = typeof docMarkdown === 'string' ? docMarkdown : '';
  const blocks = [];
  if (!doc.trim()) {
    return { mapText: '[Document outline]\n(empty)', blocks: [] };
  }

  const lines = doc.split('\n');
  const sectionStarts = [];
  for (let i = 0; i < lines.length; i++) {
    if (isSectionStart(lines[i], i, lines)) sectionStarts.push(i);
  }

  let blockRanges;
  if (sectionStarts.length > 0) {
    blockRanges = [];
    if (sectionStarts[0] > 0) {
      blockRanges.push({ startLineIdx: 0, endLineIdx: sectionStarts[0] - 1 });
    }
    for (let idx = 0; idx < sectionStarts.length; idx++) {
      const endLineIdx = idx + 1 < sectionStarts.length ? sectionStarts[idx + 1] - 1 : lines.length - 1;
      blockRanges.push({ startLineIdx: sectionStarts[idx], endLineIdx });
    }
  } else {
    const paragraphs = [];
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      const trimmed = (line || '').trim();
      if (trimmed) {
        let end = i;
        while (end + 1 < lines.length && (lines[end + 1] || '').trim()) end++;
        paragraphs.push({ startLineIdx: i, endLineIdx: end });
        i = end + 1;
      } else {
        i++;
      }
    }
    blockRanges = paragraphs;
  }

  const outlineLines = ['[Document outline]'];

  for (let idx = 0; idx < blockRanges.length; idx++) {
    const range = blockRanges[idx];
    const startLineIdx = range.startLineIdx;
    const endLineIdx = range.endLineIdx;
    const blockLines = lines.slice(startLineIdx, endLineIdx + 1);
    const blockText = blockLines.join('\n');
    let startIndex = 0;
    for (let i = 0; i < startLineIdx; i++) startIndex += lines[i].length + 1;
    const endIndex = startIndex + blockText.length;

    const firstLine = blockLines[0] || '';
    const preview = firstLine.trim().slice(0, PREVIEW_MAX_LEN);
    const id = `section_${idx}`;
    blocks.push({ id, startIndex, endIndex, preview, startLineIdx, endLineIdx });
    outlineLines.push(`${id}: ${preview}${preview.length < firstLine.trim().length ? '...' : ''}`);
  }

  const mapText = outlineLines.join('\n');
  return { mapText, blocks };
}

/**
 * Apply a single block replacement: replace the span [startIndex, endIndex) for the given blockId with newContent.
 * Returns the new document string.
 */
export function applyBlockReplacement(doc, blocks, blockId, newContent) {
  const id = (blockId || '').trim().toLowerCase();
  const block = blocks.find((b) => (b.id || '').toLowerCase() === id);
  if (!block) return doc;
  const before = doc.slice(0, block.startIndex);
  const after = doc.slice(block.endIndex);
  const content = (newContent || '').trim();
  return (before + content + after).trim();
}

/**
 * Apply multiple replacements in order. Uses the current doc and block map; each replacement
 * is applied and the map is not re-parsed (indices refer to original doc). For multiple
 * replacements we apply from end to start so indices remain valid, or we could re-build
 * the map after each replace. Simpler: apply in reverse order by startIndex so earlier
 * text is not shifted for later replacements.
 */
export function applyBlockReplacements(doc, blocks, replacements) {
  if (!replacements || replacements.length === 0) return doc;
  const byId = (bid) => blocks.find((x) => (x.id || '').toLowerCase() === (bid || '').trim().toLowerCase());
  const sorted = [...replacements].sort((a, b) => {
    const blockA = byId(a.blockId);
    const blockB = byId(b.blockId);
    if (!blockA || !blockB) return 0;
    return blockB.startIndex - blockA.startIndex;
  });
  let current = doc;
  for (const r of sorted) {
    current = applyBlockReplacement(current, blocks, r.blockId, r.content);
  }
  return current;
}
